import {
  type ColumnVisibilityState,
  type Table,
  useTable,
} from '@tanstack/react-table';
import type { SearchConversationEnriched } from 'lib-search-types';
import { useMemo } from 'react';

import {
  conversationsTableColumns,
  conversationsTableFeatures,
  type ConversationsTableFeatures,
} from '../conversationsTable.columns';

import type { UseConversationsTableLayoutResult } from './useConversationsTableLayout';

const alwaysVisibleColumnIds = conversationsTableColumns.flatMap((column) =>
  column.enableHiding === false && column.id ? [column.id] : [],
);

/**
 * Returns the column and row model that the conversations table renders, in
 * the user's layout when given one and in the default layout otherwise.
 */
export const useConversationsTable = (
  conversations: readonly SearchConversationEnriched[],
  layout?: UseConversationsTableLayoutResult,
): Table<ConversationsTableFeatures, SearchConversationEnriched> => {
  const savedColumnVisibility = layout?.columnVisibility;

  // TanStack hides any column the state says to, even one that can't be hidden.
  const columnVisibility = useMemo(
    (): ColumnVisibilityState | undefined =>
      savedColumnVisibility && {
        ...savedColumnVisibility,
        ...Object.fromEntries(
          alwaysVisibleColumnIds.map((columnId) => [columnId, true]),
        ),
      },
    [savedColumnVisibility],
  );

  return useTable({
    features: conversationsTableFeatures,
    data: conversations,
    columns: conversationsTableColumns,
    getRowId: (conversation) => conversation.id.toString(),
    state: {
      columnOrder: layout?.columnOrder,
      columnVisibility,
    },
    meta: { layout },
  });
};
